/**
 * PwaService
 *
 * Purpose:
 *   Answers "is this app already installed?" for the UI. The install
 *   instructions ({@link PwaInstructionsComponent}) only make sense to someone
 *   running in a browser tab, so any prompt that offers them is gated on this.
 *
 * Responsibility:
 *   Installed-state detection, and the one-time toast that offers the
 *   instructions to someone who could act on them. The update lifecycle (a new
 *   build is ready, prompt to reload) belongs to {@link PwaUpdateService} — this
 *   service is deliberately separate from it and holds no service-worker
 *   dependency, so it answers the same way in local dev where the service worker
 *   is disabled.
 *
 * Notes:
 *   - There is no way to ask the browser "is my app installed?" from a normal
 *     tab. What we can detect is whether THIS window is the installed one, which
 *     is what the display mode tells us. A user who installed the app and then
 *     visits the site in Safari still reads as "not installed" here — showing
 *     them the instructions again is the harmless direction to be wrong in.
 *   - iOS Safari did not support the display-mode media query until 16.4, so the
 *     legacy `navigator.standalone` flag is checked as a fallback. It exists
 *     only on iOS, so it is read off a cast rather than the typed Navigator.
 *   - This app uses Ionic via NgModule (IonicModule.forRoot), so ToastController
 *     is imported from '@ionic/angular' (not the standalone entrypoint).
 */

import { Injectable, inject } from '@angular/core';
import { Platform, ToastController } from '@ionic/angular';

@Injectable({
  providedIn: 'root',
})
export class PwaService {
  // #region Fields
  private readonly _toastController = inject(ToastController);
  private readonly _platform = inject(Platform);

  /** localStorage key: the user acted on the prompt (tapped through, or closed it). */
  private readonly _handledKey = 'pwaInstallPromptHandled';
  /** localStorage key: how many times the prompt has been shown without being acted on. */
  private readonly _shownCountKey = 'pwaInstallPromptShownCount';

  /**
   * How many times the toast may appear before we stop offering it. A prompt
   * that times out unacknowledged shouldn't be treated as a "no", but it
   * shouldn't reappear forever either.
   */
  private readonly _maxPrompts = 3;

  /** Auto-dismiss (ms). Long enough to read, short enough to not sit on the UI. */
  private readonly _toastDurationMs = 12000;
  // #endregion

  // #region Installed-state detection
  /**
   * Whether the app is running as an installed PWA rather than in a browser tab.
   *
   * Checks the standard `display-mode` media query first (Chrome/Edge/Android,
   * and iOS 16.4+), then falls back to the legacy iOS `navigator.standalone`
   * flag. `fullscreen` and `minimal-ui` count as installed too: a manifest
   * change to either of those display modes should not make the app start
   * telling everyone to install it again.
   *
   * @returns True when the current window was launched from an installed icon.
   */
  public isRunningStandalone(): boolean {
    const installedDisplayModes = ['standalone', 'fullscreen', 'minimal-ui'];
    const matchesInstalledDisplayMode = installedDisplayModes.some(
      (mode) => window.matchMedia(`(display-mode: ${mode})`).matches
    );

    return matchesInstalledDisplayMode || this.isIosStandalone();
  }

  /**
   * Inverse of {@link isRunningStandalone}, for readability in templates —
   * `*ngIf="canInstall"` reads better than a negated call.
   *
   * Note this says nothing about whether the CURRENT browser can install
   * anything — for that (and for anything user-facing) use
   * {@link canAddToHomeScreen}.
   *
   * @returns True when the app is in a browser tab and could still be installed.
   */
  public get canInstall(): boolean {
    return !this.isRunningStandalone();
  }

  /**
   * Whether to offer "add to your home screen" here at all: not already
   * installed, AND in a browser that can actually do it (see
   * {@link isIosSafari}).
   *
   * This is the gate for any UI that offers the steps unasked — the toast and
   * the landing page's button both use it. Desktop is excluded deliberately:
   * desktop Chrome installs from an address-bar icon, not a share sheet, so our
   * instructions (five iPhone screenshots) would not describe what the user is
   * looking at. A desktop user who wants them still has the menu's Help item,
   * which is phrased as installing on your phone, and the /install-help link.
   *
   * @returns True when this browser could add the app to its home screen.
   */
  public get canAddToHomeScreen(): boolean {
    return this.canInstall && this.isIosSafari();
  }

  /**
   * The legacy iOS-only standalone flag, for Safari versions older than 16.4
   * where the `display-mode` media query is not implemented.
   *
   * @returns True when iOS reports this window as launched from the home screen.
   */
  private isIosStandalone(): boolean {
    return (window.navigator as { standalone?: boolean }).standalone === true;
  }
  // #endregion

  // #region Install prompt
  /**
   * Whether to offer the one-time "add to your home screen" toast.
   *
   * {@link canAddToHomeScreen} covers whether the offer makes sense here at all;
   * this adds the conditions specific to interrupting someone with it — they
   * have not already dealt with the prompt, and it has not been shown
   * {@link _maxPrompts} times already.
   *
   * The caller decides WHEN to ask (see AppComponent) — this only answers whether
   * asking would be useful.
   *
   * @returns True when the toast should be presented.
   */
  public shouldPromptToInstall(): boolean {
    return this.canAddToHomeScreen
      && !this.promptHandled()
      && this.promptShownCount() < this._maxPrompts;
  }

  /**
   * Presents the install toast: a bottom, auto-dismissing toast rather than an
   * alert. An alert would take the first tap from someone who opened the app to
   * log a delivery, and {@link PwaUpdateService} already owns the blocking-prompt
   * slot on startup — two dialogs racing on one cold load is worse than none.
   *
   * It auto-dismisses so it can never sit on top of the mobile flow's bottom
   * action buttons. Timing out is not treated as a refusal; the show counter is
   * what stops it eventually.
   *
   * @param onShowMeHow Invoked when the user taps through — the caller opens the
   *                    instructions (a modal, so they keep their place).
   */
  public async presentInstallPrompt(onShowMeHow: () => void): Promise<void> {
    this.recordPromptShown();

    const toast = await this._toastController.create({
      header: 'Add ReceivingMgr to your home screen',
      message: 'Open it like a normal app — full screen, no browser bars.',
      position: 'bottom',
      duration: this._toastDurationMs,
      cssClass: 'pwa-install-toast',
      buttons: [
        {
          text: 'Show me how',
          role: 'info',
          handler: () => {
            // Tapping through counts as dealt with either way: if they install,
            // the display-mode check hides it; if they don't, they have seen the
            // steps and chose not to, so nagging again adds nothing.
            this.markPromptHandled();
            onShowMeHow();
          },
        },
        {
          text: 'Not now',
          role: 'cancel',
          handler: () => {
            this.markPromptHandled();
          },
        },
      ],
    });

    await toast.present();
  }

  /**
   * Whether this is Safari on iOS, the only browser that can add the app to an
   * iOS home screen.
   *
   * Chrome/Firefox/Edge on iOS and the in-app webviews that Teams and Outlook
   * open links in have no "Add to Home Screen" at all, so prompting there points
   * at a button that does not exist. There is no feature test for this, so it is
   * a user-agent check: Safari proper carries a `Version/` token, which the
   * webviews omit and the wrapped browsers replace with their own (CriOS, FxiOS,
   * EdgiOS, OPiOS).
   *
   * Sniffing is fragile, so the failure direction matters: a miss shows no toast
   * and the instructions stay reachable from the menu, rather than the toast
   * appearing where it cannot be followed.
   *
   * @returns True when the current browser can install to the home screen.
   */
  private isIosSafari(): boolean {
    if (!this._platform.is('ios')) {
      return false;
    }

    const userAgent = window.navigator.userAgent;
    const isWrappedBrowser = /CriOS|FxiOS|EdgiOS|OPiOS/.test(userAgent);
    const isSafariProper = /Version\/[\d.]+/.test(userAgent) && /Safari/.test(userAgent);

    return isSafariProper && !isWrappedBrowser;
  }

  /** Whether the user has already tapped through or dismissed the prompt. */
  private promptHandled(): boolean {
    return localStorage.getItem(this._handledKey) === 'true';
  }

  /** Records that the user dealt with the prompt, so it is not offered again. */
  private markPromptHandled(): void {
    localStorage.setItem(this._handledKey, 'true');
  }

  /** How many times the prompt has been shown and left unacknowledged. */
  private promptShownCount(): number {
    const stored = Number(localStorage.getItem(this._shownCountKey));
    return Number.isFinite(stored) ? stored : 0;
  }

  /** Increments the show counter that eventually retires the prompt. */
  private recordPromptShown(): void {
    localStorage.setItem(this._shownCountKey, String(this.promptShownCount() + 1));
  }
  // #endregion
}
